import { z } from 'zod';

/**
 * Build the `views` field schema. Element type is parameterized so the
 * permissive base schema (used for runtime type inference) and the factory
 * (used for the LLM-facing JSON Schema) share a single source of truth for
 * the field's `.min(1)` constraint and its `.describe()` text.
 */
const viewsField = (element: z.ZodType<string>) =>
  element.array().min(1).describe('View externalIds to describe. Must be drawn from the available views enum.');

/**
 * Permissive shape for {@link DescribeViewsInput} type inference. Validates
 * the runtime payload structure (`{ views: string[] }`) without constraining
 * which specific externalIds are accepted — the dynamic enum constraint lives
 * in {@link buildDescribeViewsInputSchema}, which extends this with a tighter
 * `views` enum at factory time.
 */
const DescribeViewsInputBaseSchema = z.object({
  views: viewsField(z.string()),
});

/**
 * Build the LLM-facing input schema for {@link DescribeViewsTool}.
 *
 * The `views` field is a dynamic enum derived from the available
 * {@link DataModelView} externalIds at factory time — this constrains the LLM
 * to only request schemas for views that are actually mounted, and surfaces
 * the full catalog directly in the JSON Schema so the LLM can pick by name.
 * The runtime shape stays compatible with {@link DescribeViewsInput} (string
 * array); only the JSON Schema projection differs.
 *
 * Zod's `z.enum` requires a non-empty tuple, so callers must gate on
 * `viewIds.length > 0` before constructing the schema.
 */
export function buildDescribeViewsInputSchema(viewIds: readonly [string, ...string[]]) {
  return DescribeViewsInputBaseSchema.extend({
    views: viewsField(z.enum(viewIds)),
  });
}

/** @public */
export type DescribeViewsInput = z.infer<typeof DescribeViewsInputBaseSchema>;

/** @public */
export type DescribeViewsOutput = {
  success: true;
};
